import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";

import { useCreateClaimDisputeMutation } from "../../store/Company/companyApiSlice";
import { uploadFilePath } from "../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../utils/uploadFileToS3";
import { validateEmail } from "../../utils/Helper";
import { digitsOf } from "./useClaimFlow";
import { extractCountryCodeAndPhoneCode } from "../../utils/countriesState";

/* ICC-11 — the dispute raised by someone who says a claimed company is
 * theirs. It is not a claim: nothing changes hands here. The form opens a
 * support ticket (ICC-12) and support investigates.
 *
 * The reason is the one answer support cannot work without, so it is the
 * only long field and the only one that gates the button alongside the
 * contact details.
 */

/* Enough to be a sentence, not an essay — the backend caps at 5000. */
export const REASON_MIN = 20;
export const REASON_MAX = 5000;

export default function useClaimDispute({ company } = {}) {
  const { token, userInfo } = useSelector((state) => state.auth);
  const [submitDispute, { isLoading: submitting }] = useCreateClaimDisputeMutation();

  /* Prefilled from the account, as the ticket asks, but editable: the person
     disputing may hold a different work address from the one they signed up
     with, and support replies to what they type here. */
  const [fullName, setFullName] = useState(userInfo?.name || "");
  const [contactEmail, setContactEmail] = useState(userInfo?.email || "");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("");
  /* The number is split across two controls, exactly as the claim flow's
     contact step does it: support has to be able to ring back, and a bare
     local number does not say which country to ring. */
  const [dialCountry, setDialCountry] = useState("AE");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState(null);

  const [apiError, setApiError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const companyID = company?.companyID || company?.data?.companyID || "";
  const companyName =
    company?.registered_company_name || company?.data?.registered_company_name || "";

  /* extractCountryCodeAndPhoneCode returns { iso2, phoneCode }. */
  const dialCodeOptions = useMemo(
    () =>
      (extractCountryCodeAndPhoneCode() || []).map((c) => ({
        value: c.iso2,
        label: `${c.iso2} (${c.phoneCode})`,
        phoneCode: c.phoneCode,
      })),
    []
  );

  const dialCode = useMemo(
    () => dialCodeOptions.find((o) => o.value === dialCountry)?.phoneCode || "+971",
    [dialCodeOptions, dialCountry]
  );

  const valid = useMemo(
    () =>
      Boolean(
        fullName.trim() &&
          validateEmail(contactEmail) &&
          reason.trim().length >= REASON_MIN &&
          reason.length <= REASON_MAX
      ),
    [fullName, contactEmail, reason]
  );

  /* Uploaded before the dispute is posted rather than after: the ticket
     carries the URL, and a ticket that references a file we failed to store
     is worse than no file at all.
     Sent as a "tickets" upload with no companyID: the disputer can't edit
     the company, so a company-owned category answers 403. Support copies it
     into the company's documents if the dispute is approved. */
  const uploadEvidence = async () => {
    if (!evidence) return { ok: true, url: "" };
    try {
      setUploading(true);
      const uploaded = await uploadFileToS3(
        uploadFilePath?.supportScreenshot,
        evidence,
        token,
        undefined,
        "tickets"
      );
      return { ok: true, url: uploaded?.url || "" };
    } catch (err) {
      setApiError(
        err?.data
          ? Object.values(err.data)[0]
          : "The supporting document could not be uploaded."
      );
      return { ok: false, url: "" };
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!valid) return;
    setApiError("");

    const { ok, url } = await uploadEvidence();
    if (!ok) return;

    try {
      const response = await submitDispute({
        data: {
          companyID,
          reason: reason.trim(),
          full_name: fullName.trim(),
          designation: designation.trim(),
          contact_email: contactEmail.trim(),
          /* E.164 with no separator, as the claim payload sends it —
             dial code included, so support knows what to dial. */
          phone: phone ? `${dialCode}${digitsOf(phone)}` : "",
          evidence_url: url,
        },
        token,
      });

      if (response?.data?.dispute_id) {
        ReactGA.event({
          category: "Claim",
          action: "Claim Dispute:Submitted",
          label: companyID,
        });
        setTicketId(response.data.ticket_id ?? null);
        setSubmitted(true);
        return;
      }

      /* The endpoint answers refusals with a `code` and a message written for
         the person reading it — an open dispute, a company that is not
         claimed, a company the viewer already manages. Showing its own words
         beats restating them less accurately here. */
      const payload = response?.error?.data;
      setApiError(
        payload?.message ||
          (payload ? Object.values(payload)[0] : "") ||
          "Something went wrong. Please try again."
      );
    } catch (err) {
      setApiError(
        err?.data?.message ||
          (err?.data ? Object.values(err.data)[0] : "") ||
          "Something went wrong. Please try again."
      );
    }
  };

  return {
    companyID,
    companyName,

    fullName,
    setFullName,
    contactEmail,
    setContactEmail,
    designation,
    setDesignation,
    phone,
    dialCountry,
    setDialCountry,
    dialCode,
    dialCodeOptions,
    setPhone,
    reason,
    setReason,
    evidence,
    setEvidence,

    valid,
    isBusy: submitting || uploading,
    apiError,
    submitted,
    ticketId,
    submit,
  };
}
